import { Box, Card, Grid, Typography, Divider, Stack } from '@mui/material';
import {
  Datagrid,
  FunctionField,
  ListBase,
  useListContext,
  useRecordContext,
  useTranslate,
} from 'react-admin';
import XTextField from '../../components/XTextField';
import XDateField from '../../components/XDateField';
import XCurrencyField from '../../components/XCurrencyField';
import IdField from '../../components/IdField';
import AddressField from '../../components/AddressField';
import XChainField from '../../components/XChainField';
import { useEffect, useState } from 'react';
import { EmptyContent } from 'src/components/empty-content';

interface Props {
  onWalletSelect: (selectedAddresses: any[]) => void;
  tabActive: string;
  record: any;
}

const WalletBulkActions = ({
  onWalletSelect,
  wallets,
}: {
  onWalletSelect: (wallets: any[]) => void;
  wallets: any[];
}) => {
  const { selectedIds } = useListContext();

  useEffect(() => {
    if (!wallets || wallets.length === 0) return;
    const selectedWallets =
      selectedIds.length > 0
        ? wallets.filter((wallet) => selectedIds.includes(wallet.id))
        : wallets;
    onWalletSelect(selectedWallets as string[]);
  }, [selectedIds, onWalletSelect, wallets]);

  return null;
};

const WalletList = ({ onWalletSelect, record, tabActive }: Props) => {
  const t = useTranslate();
  const [walletsList, setWalletsList] = useState<any[]>([]);

  useEffect(() => {
    if (!record || !record.data) return;
    const filteredWallets = record?.data.wallets.filter(
      (w: any) =>
        (w.chainId === 0 && tabActive === 'dex' && w.tradeType !== 'PREDICT') || (w.chainId !== 0 && tabActive === 'meme' && w.tradeType !== 'PREDICT') || (w.tradeType === 'PREDICT' && tabActive === 'predict')
    );
    setWalletsList(filteredWallets);
  }, [record, tabActive]);
  if (!record || !record.data) return null;

  return (
    <Box>
      {/* Wallets List */}
      <Stack >
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            {t('module.userInfo.walletsManagement')} ({walletsList.length})
          </Typography>
          <ListBase>
            <Datagrid
              rowClick={false}
              bulkActionButtons={
                <WalletBulkActions onWalletSelect={onWalletSelect} wallets={walletsList || []} />
              }
              data={walletsList || []}
              empty={<EmptyContent />}
              sx={{
                '& .MuiTableCell-root': {
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <FunctionField
                label={t('module.userInfo.wallet.address')}
                render={(wallet: any) => (
                  <AddressField address={wallet.walletAddress} sx={{ width: '180px' }} />
                )}
              />
              <FunctionField
                label={t('module.userInfo.wallet.chain')}
                render={(wallet: any) => (
                  <XChainField value={wallet.chainId} sx={{ width: '80px' }} />
                )}
              />
              <FunctionField
                label={t('module.userInfo.wallet.buy')}
                render={(wallet: any) => {
                  const value = (tabActive === 'dex' && wallet.totalBuyDex) || (tabActive === 'meme' && wallet.totalBuyMeme) || (tabActive === 'predict' && wallet.totalBuyPredict) || 0
                  return (
                    <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={value}
                    sx={{ width: '120px', fontWeight: 600 }}
                  />
                  )
                }}
              />
              <FunctionField
                label={t('module.userInfo.wallet.sell')}
                render={(wallet: any) => {
                  const value = (tabActive === 'dex' && wallet.totalSellDex) || (tabActive === 'meme' && wallet.totalSellMeme) || (tabActive === 'predict' && wallet.totalSellPredict) || 0
                  return (
                    <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={value}
                    sx={{ width: '120px', fontWeight: 600 }}
                  />
                  )
                }}
              />
              <FunctionField
                label={t('module.userInfo.wallet.deposit')}
                render={(wallet: any) => {
                  const value = (tabActive === 'dex' && wallet.totalDepositDex) || (tabActive === 'meme' && wallet.totalDepositMeme) || (tabActive === 'predict' && wallet.totalDepositPredict) || 0
                  return (
                    <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={value}
                    sx={{ width: '120px', fontWeight: 600 }}
                  />
                  )
                }}
              />
              <FunctionField
                label={t('module.userInfo.wallet.withdraw')}
                render={(wallet: any) => {
                  const value = (tabActive === 'dex' && wallet.totalWithdrawDex) || (tabActive === 'meme' && wallet.totalWithdrawMeme) || (tabActive === 'predict' && wallet.totalWithdrawPredict) || 0
                  return (
                    <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={value}
                    sx={{ width: '120px', fontWeight: 600 }}
                  />
                  )
                }}
              />
              <FunctionField
                label={t('module.userInfo.wallet.balance')}
                render={(wallet: any) => {
                  const value = (tabActive === 'dex' && wallet.balanceDex) || (tabActive === 'meme' && wallet.balanceMeme) || (tabActive === 'predict' && wallet.balancePredict) || 0
                  return (
                    <XCurrencyField
                    currency={{ symbol: '$', position: 'before' }}
                    value={value}
                    sx={{ width: '120px', fontWeight: 600 }}
                  />
                  )
                }}
              />
              <FunctionField
                label={t('module.userInfo.wallet.pnl')}
                render={(wallet: any) => {
                  const value = (tabActive === 'dex' && wallet.totalPnlDex) || (tabActive === 'meme' && wallet.totalPnlMeme) || (tabActive === 'predict' && wallet.totalPnlPredict) || 0
                  return (
                    <XCurrencyField
                    colorize
                    currency={{ symbol: '$', position: 'before' }}
                    value={value}
                    sx={{ width: '120px' }}
                  />
                  )
                }}
              />
            </Datagrid>
          </ListBase>
        </Card>
      </Stack>
    </Box>
  );
};

export default WalletList;
